import { styled } from "@linaria/react"
import { runInAction } from "mobx"
import { observer } from "mobx-react-lite"
import { useContext } from "react"
import { useHotkeys } from "react-hotkeys-hook"

import { Button } from "@/components/Button/Button.tsx"
import { CopyableCodeSnippet } from "@/components/CopyableCodeSnippet/CopyableCodeSnippet.tsx"
import { Dialog } from "@/components/Dialog/Dialog.tsx"
import { Gap, Horizontal, Vertical } from "@/components/Flex.ts"
import { Scrollable } from "@/components/Scrollable/Scrollable.tsx"
import { ScrollableModelImpl } from "@/components/Scrollable/ScrollableModel.ts"
import { SecondaryTextSpan } from "@/components/SecondaryTextSpan/SecondaryTextSpan.ts"
import { Spinner } from "@/components/Spinner/Spinner.tsx"
import * as Tabs from "@/components/Tabs/Tabs.ts"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { useDevMode } from "@/hooks/useDevMode.ts"
import { useModel } from "@/hooks/useModel.ts"
import { CloseLight, FullScreenRegular, Icon16, Icon20, TerminalRegular } from "@/icons/Icons.ts"
import { copyToClipboard } from "@/lib/std.ts"
import { DevToolsPanelModel } from "@/routes/tasks/TaskViewScene/DevToolsPanelModel.ts"
import { PreviewWidgetContent } from "@/routes/tasks/TaskViewScene/PreviewWidgetContent.tsx"
import { SshKeysOnboarding } from "@/routes/tasks/TaskViewScene/SshKeysOnboarding.tsx"
import { TaskPreviewTerminal } from "@/routes/tasks/TaskViewScene/TaskPreviewTerminal.tsx"
import { TaskViewModelContext } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"
import * as textStyles from "@/styles/textStyles.ts"
import { ellipsize } from "@/styles/textStyles.ts"

import type { PromptInputModel } from "@/components/PromptInput/PromptInputModel.ts"
import type { TaskViewModel } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"
import type { TerminalModel } from "@/routes/tasks/TaskViewScene/TerminalModel.ts"
import type { IObservableValue } from "mobx"

const TabId = {
  Logs: "logs",
  Log: (logId: string) => `log:${logId}`,
  Errors: "errors",
  SshCommands: "ssh-commands",
  Status: "status",
}

export const DevToolsPanel = observer((props: {
  errors?: IObservableValue<Map<string, Error>>
  setDevToolsOpen: (open: boolean) => void
  openLogId: IObservableValue<string | null>
}) => {
  const taskViewModel = useContext(TaskViewModelContext)

  const model = useModel(() => new DevToolsPanelModel(
    taskViewModel,
    props.openLogId,
    props.errors
  ), [taskViewModel, props.openLogId, props.errors])

  return (
    <DevToolsPanelContainer>
      <DevToolsContent model={model} onClose={() => props.setDevToolsOpen(false)} />
    </DevToolsPanelContainer>
  )
})

const DevToolsContent = observer(({ model, onClose }: {
  model: DevToolsPanelModel
  onClose: () => void
}) => {
  const taskViewModel = useContext(TaskViewModelContext)
  const previewModel = taskViewModel?.currentPreview.get() || null
  const devMode = useDevMode()

  const errorsScrollableModel = useModel(() => new ScrollableModelImpl())
  const sshCommandsScrollableModel = useModel(() => new ScrollableModelImpl())
  const statusScrollableModel = useModel(() => new ScrollableModelImpl())

  const errors = Array.from(model.errors?.get().values() ?? [])
  const terminalModels = model.getTerminalModels()

  const connectCommand = model.connectCommand.get()
  const tunnelCommand = model.tunnelCommand.get()

  useHotkeys("Esc", () => onClose())

  return (
    <TabsContainer>
      <TabsRoot value={model.currentTab.get()} onValueChange={model.setCurrentTab}>
        <TabsHeader>
          <Tabs.List aria-label="Developer tools">
            {terminalModels.map(terminalModel =>
              <Tabs.Trigger key={terminalModel.logModel?.id} value={TabId.Log(terminalModel.logModel?.id || "")}>
                <Horizontal alignItems="center" gap="4px">
                  <TerminalRegular className={Icon16} />
                  {terminalModel.logModel?.label}
                </Horizontal>
              </Tabs.Trigger>
            )}

            {terminalModels.length == 0 && (
              <Tabs.Trigger value={TabId.Logs}>
                <Horizontal alignItems="center" gap="4px">
                  <TerminalRegular className={Icon16} />
                  Terminal output
                </Horizontal>
              </Tabs.Trigger>
            )}

            <Tabs.Trigger value={TabId.Errors}>
              Browser errors
              {errors.length > 0 && <TabCounter>{errors.length.toString()}</TabCounter>}
            </Tabs.Trigger>

            <Tabs.Trigger value={TabId.SshCommands}>SSH commands</Tabs.Trigger>

            {devMode && (
              <Tabs.Trigger value={TabId.Status}>Preview status</Tabs.Trigger>
            )}
          </Tabs.List>
          <Tooltip content="Close" align="end">
            <CloseButton kind="icon" onClick={onClose}><CloseLight className={Icon20} /></CloseButton>
          </Tooltip>
        </TabsHeader>

        <Gap height="16px" />

        {terminalModels.map(terminalModel =>
          <LogTab
            key={terminalModel.logModel?.id}
            terminalModel={terminalModel}
            inputModel={taskViewModel?.inputModel || null}
            panelModel={model}
          />
        )}

        {terminalModels.length == 0 && (
          <DevToolsTabContent value={TabId.Logs}>
            <SecondaryTextSpan>No logs</SecondaryTextSpan>
          </DevToolsTabContent>
        )}

        <DevToolsTabContent value={TabId.Errors}>
          <Scrollable model={errorsScrollableModel}>
            <Vertical gap="16px">
              {errors.length === 0 && <SecondaryTextSpan>No errors</SecondaryTextSpan>}
              {errors.map((error, i) => (
                <Error key={`error-${i}`} error={error} index={i} model={taskViewModel} />
              ))}
            </Vertical>
          </Scrollable>
        </DevToolsTabContent>

        <DevToolsTabContent value={TabId.SshCommands}>
          <Scrollable model={sshCommandsScrollableModel}>
            <Vertical gap="24px" alignItems="flex-start" alignSelf="stretch">
              {model.sshConnectionLoading.get() ? (
                <Spinner text={ellipsize("Loading SSH commands")} />
              ) : (
                model.keyCount.get() > 0 ? (
                  <>
                    <Vertical gap="8px" alignItems="flex-start" alignSelf="stretch">
                      <SshCommandLabel>Set up an SSH connection to the preview environment:</SshCommandLabel>
                      {connectCommand
                        ? <CopyableCodeSnippet text={connectCommand} language="sh" />
                        : <SecondaryTextSpan>No SSH connect command available</SecondaryTextSpan>
                      }
                    </Vertical>

                    <Vertical gap="8px" alignItems="flex-start" alignSelf="stretch">
                      <SshCommandLabel>Set up an SSH tunnel to the preview environment:</SshCommandLabel>
                      {tunnelCommand
                        ? <CopyableCodeSnippet text={tunnelCommand} language="sh" />
                        : <SecondaryTextSpan>No SSH tunnel command available</SecondaryTextSpan>
                      }
                    </Vertical>
                  </>
                ) : (
                  <SshKeysOnboarding onComplete={() => model.refetchSshCommands()} />
                )
              )}
            </Vertical>
          </Scrollable>
        </DevToolsTabContent>

        {devMode && (
          <DevToolsTabContent value={TabId.Status}>
            <Scrollable model={statusScrollableModel}>
              {previewModel
                ? (
                  <PreviewWidgetContentContainer>
                    <PreviewWidgetContent model={previewModel} width="auto" />
                  </PreviewWidgetContentContainer>
                )
                : <SecondaryTextSpan>No status available</SecondaryTextSpan>
              }
            </Scrollable>
          </DevToolsTabContent>
        )}
      </TabsRoot>
    </TabsContainer>
  )
})

const LogTab = observer((props: {
  terminalModel: TerminalModel
  inputModel: PromptInputModel | null
  panelModel: DevToolsPanelModel
}) => {
  const logId = props.terminalModel.logModel?.id || ""
  const dialogOpen = props.panelModel.getDialogOpen(logId)

  return (
    <DevToolsTabContent value={TabId.Log(logId)}>
      <TerminalContainer>
        <TaskPreviewTerminal model={props.terminalModel} />
        <Horizontal gap="8px">
          <Button onClick={() => {
            const inputModel = props.inputModel
            const prompt = inputModel?.firstInputValue

            if (inputModel && prompt) {
              runInAction(() => {
                const terminalOutput = props.terminalModel.getLastLinesOrSelection(100) ?? ""
                if (prompt.get().length > 0) {
                  prompt.set(prompt.get() + "\n\n" + terminalOutput)
                } else {
                  prompt.set(terminalOutput)
                }

                inputModel.highlight()
              })
            }
          }}>Copy to chat</Button>

          <Button
            kind="alternative"
            onClick={() => copyToClipboard(props.terminalModel.getLastLinesOrSelection(100) ?? "")}
          >
            Copy to clipboard
          </Button>

          <Dialog
            open={dialogOpen}
            onOpenChange={(open) => props.panelModel.setDialogOpen(logId, open)}
            trigger={(
              <Button kind="alternative" onClick={() => props.panelModel.setDialogOpen(logId, true)}>
                <Horizontal alignItems="center" gap="4px">
                  <FullScreenRegular className={Icon16} />
                  Open in full screen
                </Horizontal>
              </Button>
            )}
            title={props.terminalModel.logModel?.label}
            maxWidth="100%"
          >
            <TaskPreviewTerminal model={props.terminalModel} />
          </Dialog>
        </Horizontal>
      </TerminalContainer>
    </DevToolsTabContent>
  )
})

const Error = observer((props: {
  error: Error
  index: number
  model: TaskViewModel | null
}) => {
  const message = props.error.message
  const stack = props.error.stack
  const error = stack && message.includes(stack) ? message : message + (stack ?? "")

  const inputModel = props.model?.inputModel
  const canSubmit = inputModel?.canSubmit.get() ?? false
  const prompt = inputModel?.firstInputValue

  return (
    <Vertical alignItems="flex-start" gap="8px">
      <Message><ErrorNumber>{props.index + 1}.</ErrorNumber> {message.trim()}</Message>
      {stack && !message.includes(stack) && <Stack>{stack}</Stack>}
      <Horizontal gap="8px">
        <Button onClick={() => {
          if (inputModel && prompt) {
            runInAction(() => {
              if (prompt.get().length > 0) {
                prompt.set(prompt.get() + "\n\n" + error)
              } else {
                prompt.set(error)
              }

              inputModel.highlight()

              if (canSubmit) {
                void inputModel.doSubmit()
              }
            })
          }
        }}>{canSubmit ? "Attempt to fix" : "Copy to chat"}</Button>

        <Button kind="alternative" onClick={() => copyToClipboard(error)}>Copy to clipboard</Button>
      </Horizontal>
    </Vertical>
  )
})

const DevToolsPanelContainer = styled(Vertical)`
  height: 50%;
  overflow: hidden;

  background: var(--text-color-3);
  border-radius: var(--border-radius-16);
  margin-top: 10px;
  padding: 16px 24px;
`

const TabsHeader = styled(Horizontal)`
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  gap: 8px;
`

const TabCounter = styled.span`
  margin-left: 4px;
  font-variant-numeric: tabular-nums;

  color: var(--error-color);
  font-weight: 600;
`

const ErrorNumber = styled.strong`
  user-select: none;
`

const Message = styled.span`
  ${textStyles.breakWord};

  white-space: pre-wrap;
`

const Stack = styled.pre`
  ${textStyles.pre};
`

const TabsContainer = styled(Vertical)`
  height: 100%;
  min-height: 0;

  overflow: hidden;
`

const TabsRoot = styled(Tabs.Root)`
  display: flex;
  flex-direction: column;

  height: 100%;
  min-height: 0;

  overflow: hidden;
`

const TerminalContainer = styled(Vertical)`
  flex: 1 1 auto;
  align-items: flex-start;
  gap: 16px;

  min-height: 0;
`

const PreviewWidgetContentContainer = styled(Horizontal)`
  justify-content: center;
`

const SshCommandLabel = styled.span`
  font-weight: 400;
  color: var(--text-color);
`

const DevToolsTabContent = styled(Tabs.Content)`
  outline: none;

  &[data-state="inactive"] {
    display: none;
  }

  &[data-state="active"] {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
  }
`

const CloseButton = styled(Button)`
  width: 20px;
  height: 20px;
`
